/**
 * The planner the assistant actually uses.
 *
 * Skill selection is deterministic phrase matching (see `deterministicPlanner`).
 * The seam is kept so a model-backed planner can be slotted in later without the
 * skills themselves changing — but no model client ships today: the long tail of
 * questions the phrase matcher cannot place is handled by the backend agent
 * (`POST /api/assistant/act`), not by a skill-selection endpoint. A model may
 * still be injected here (tests do so) and, when enabled, is tried first with the
 * deterministic matcher underneath as the fallback.
 *
 * The fallback is used whenever the model does not produce a usable answer, and
 * "usable" is decided locally: a selection naming a skill the account was not
 * offered is discarded the same as an error. A failed model call is therefore
 * never visible to the user as a failure, only as a slightly less clever answer.
 */

import { deterministicPlanner } from './deterministicPlanner';
import type { AssistantPlanner, PlannerTool, QuestionPlan } from './types';
import type { AssistantAudience } from '../assistantCapabilities';

export type { AssistantPlanner, PlannerTool, QuestionPlan } from './types';
export { deterministicPlanner } from './deterministicPlanner';

export interface CreatePlannerOptions {
  /**
   * Whether an injected model-backed planner may be used. Off by default, and
   * there is no built-in model to reach for: the backend has no skill-selection
   * endpoint, so a caller that wants one must supply it.
   */
  useModel?: boolean;
  /** Injected model planner. Only consulted when {@link useModel} is true. */
  model?: AssistantPlanner;
  fallback?: AssistantPlanner;
}

export const createPlanner = ({
  useModel = false,
  model,
  fallback = deterministicPlanner,
}: CreatePlannerOptions = {}): AssistantPlanner => ({
  plan: async (
    message: string,
    tools: PlannerTool[],
    audience: AssistantAudience,
  ): Promise<QuestionPlan | null> => {
    if (useModel && model) {
      try {
        const planned = await model.plan(message, tools, audience);
        if (planned) return planned;
      } catch {
        // Deliberately swallowed. A planner outage should degrade the quality
        // of question matching, not surface as an error on a question the
        // phrase matcher can still answer perfectly well.
      }
    }

    return fallback.plan(message, tools, audience);
  },
});

export default createPlanner;
